import PropTypes from 'prop-types'
import classNames from 'classnames'
import styles from './styles/Icon.css'
import ICON_MAP from './icon-mapping'

/**
 * Icon component using @central-icons-react
 *
 * Usage:
 *   <Icon name="chat" />
 *   <Icon name="table" size={20} />
 *   <ChatIcon size={16} color="#333" />
 */

const Icon = ({ name, size = 24, color = 'currentColor', className, strokeWidth, ...rest }) => {
  const CentralComponent = ICON_MAP[name]

  if (CentralComponent) {
    return <CentralComponent size={size} color={color} className={classNames(styles.icon, className)} {...rest} />
  }

  console.warn(`Icon "${name}" not found.`)
  return null
}

Icon.propTypes = {
  name: PropTypes.string.isRequired,
  size: PropTypes.number,
  color: PropTypes.string,
  className: PropTypes.string,
  strokeWidth: PropTypes.number
}

// ============================================
// NAMED EXPORTS - for convenient imports
// ============================================

// Chat & Messaging
export const ChatIcon = props => <Icon name='chat' {...props} />
export const NewChatIcon = props => <Icon name='newChat' {...props} />

// Table & Data
export const TableIcon = props => <Icon name='table' {...props} />

// Actions
export const TrashIcon = props => <Icon name='trash' {...props} />
export const EditIcon = props => <Icon name='edit' {...props} />
export const ShareIcon = props => <Icon name='share' {...props} />
export const CopyIcon = props => <Icon name='copy' {...props} />
export const DownloadIcon = props => <Icon name='download' {...props} />
export const SearchIcon = props => <Icon name='search' {...props} />
export const PlusIcon = props => <Icon name='plus' {...props} />
export const MinusIcon = props => <Icon name='minus' {...props} />
export const XIcon = props => <Icon name='x' {...props} />
export const CheckIcon = props => <Icon name='check' {...props} />
export const ExternalLinkIcon = props => <Icon name='externalLink' {...props} />

// UI Elements
export const HelpIcon = props => <Icon name='help' {...props} />
export const InfoIcon = props => <Icon name='info' {...props} />
export const WarningIcon = props => <Icon name='warning' {...props} />
export const SettingsIcon = props => <Icon name='settings' {...props} />
export const SparklesIcon = props => <Icon name='sparkles' {...props} />

// Navigation
export const ChevronRightIcon = props => <Icon name='chevronRight' {...props} />
export const ChevronLeftIcon = props => <Icon name='chevronLeft' {...props} />
export const ChevronDownIcon = props => <Icon name='chevronDown' {...props} />
export const ChevronUpIcon = props => <Icon name='chevronUp' {...props} />
export const MinimizeIcon = props => <Icon name='minimize' {...props} />
export const MaximizeIcon = props => <Icon name='maximize' {...props} />

// Menus
export const MoreVerticalIcon = props => <Icon name='moreVertical' {...props} />
export const MoreHorizontalIcon = props => <Icon name='moreHorizontal' {...props} />

// People & roles
export const UserIcon = props => <Icon name='user' {...props} />
export const UsersIcon = props => <Icon name='users' {...props} />
export const UserPlusIcon = props => <Icon name='userPlus' {...props} />
export const UserRemoveIcon = props => <Icon name='userRemove' {...props} />
export const ShieldCheckIcon = props => <Icon name='shieldCheck' {...props} />
export const ClockIcon = props => <Icon name='clock' {...props} />

// Misc
export const BookmarkIcon = props => <Icon name='bookmark' {...props} />
export const BookmarkCheckIcon = props => <Icon name='bookmarkCheck' {...props} />
export const HistoryIcon = props => <Icon name='history' {...props} />
export const FolderIcon = props => <Icon name='folder' {...props} />
export const EyeIcon = props => <Icon name='eye' {...props} />
export const EyeOffIcon = props => <Icon name='eyeOff' {...props} />
export const TagIcon = props => <Icon name='tag' {...props} />
export const BellIcon = props => <Icon name='bell' {...props} />
export const LightbulbIcon = props => <Icon name='lightbulb' {...props} />
export const SearchChecklistIcon = props => <Icon name='searchChecklist' {...props} />
export const DashboardIcon = props => <Icon name='dashboardLayout' {...props} />
export const PatentsIcon = props => <Icon name='patents' {...props} />
export const JournalsIcon = props => <Icon name='journals' {...props} />
export const OrganizationsIcon = props => <Icon name='organizations' {...props} />
export const FundersIcon = props => <Icon name='funders' {...props} />
export const BrowserExtensionIcon = props => <Icon name='browserExtension' {...props} />
export const ApiIcon = props => <Icon name='api' {...props} />
export const McpIcon = props => <Icon name='mcp' {...props} />
export const DemoIcon = props => <Icon name='demo' {...props} />
export const BioIcon = props => <Icon name='bio' {...props} />
export const MetricsIcon = props => <Icon name='metrics' {...props} />
export const SubscriptionIcon = props => <Icon name='subscription' {...props} />
export const AccountSettingsIcon = props => <Icon name='accountSettings' {...props} />
export const SignOutIcon = props => <Icon name='signOut' {...props} />
export const ShareRightIcon = props => <Icon name='shareRight' {...props} />
export const ExportIcon = props => <Icon name='export' {...props} />
export const RegenerateIcon = props => <Icon name='regenerate' {...props} />
export const LayoutGridIcon = props => <Icon name='layoutGrid' {...props} />
export const BulletListIcon = props => <Icon name='bulletList' {...props} />
export const ZapIcon = props => <Icon name='zap' {...props} />

// Branding
export const SciteLogo = ({ height = 16, className }) => {
  const width = Math.round(height * (92 / 50))
  return (
    <svg
      width={width}
      height={height}
      viewBox='0 0 92 50'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-label='scite'
    >
      <path
        d='M10.71 32.6025C10.8353 34.4295 11.5868 36.0675 12.9647 37.5165C14.3426 38.9655 16.4095 39.69 19.1653 39.69C21.2321 39.69 22.7666 39.249 23.7687 38.367C24.7708 37.422 25.2718 36.3195 25.2718 35.0595C25.2718 33.9885 24.8961 33.075 24.1445 32.319C23.3929 31.5 22.1089 30.9015 20.2926 30.5235L14.1861 29.106C9.67658 28.098 6.38842 26.3655 4.32158 23.9085C2.25474 21.3885 1.22132 18.4905 1.22132 15.2145C1.22132 13.1355 1.65974 11.1825 2.53658 9.3555C3.41342 7.4655 4.60342 5.859 6.10658 4.536C7.67237 3.15 9.48869 2.0475 11.5555 1.2285C13.685 0.409498 16.0024 0 18.5076 0C21.7645 0 24.5203 0.4725 26.775 1.4175C29.0297 2.2995 30.8461 3.4335 32.2239 4.8195C33.6645 6.2055 34.7292 7.686 35.4182 9.261C36.1071 10.773 36.5142 12.159 36.6395 13.419L26.2113 15.7815C26.0861 15.0885 25.8668 14.3955 25.5537 13.7025C25.3032 12.9465 24.8647 12.2535 24.2384 11.6235C23.6121 10.9935 22.8292 10.4895 21.8897 10.1115C21.0129 9.7335 19.9168 9.5445 18.6016 9.5445C16.9732 9.5445 15.564 9.9855 14.374 10.8675C13.2466 11.7495 12.6829 12.852 12.6829 14.175C12.6829 15.309 13.0587 16.2225 13.8103 16.9155C14.6245 17.6085 15.6579 18.081 16.9105 18.333L23.4868 19.7505C28.059 20.6955 31.4724 22.4595 33.7271 25.0425C36.0445 27.6255 37.2032 30.618 37.2032 34.02C37.2032 35.91 36.8274 37.7685 36.0758 39.5955C35.3868 41.4225 34.2908 43.0605 32.7876 44.5095C31.2845 45.9585 29.4055 47.124 27.1508 48.006C24.8961 48.888 22.2655 49.329 19.2592 49.329C15.7518 49.329 12.8082 48.825 10.4282 47.817C8.04816 46.809 6.10658 45.5805 4.60342 44.1315C3.10026 42.6825 1.9729 41.139 1.22132 39.501C0.532369 37.8 0.125263 36.288 0 34.965L10.71 32.6025ZM43.59 49.8015V39.501H91.5965V49.8015H43.59Z'
        fill='#0062FF'
      />
    </svg>
  )
}

SciteLogo.propTypes = {
  height: PropTypes.number,
  className: PropTypes.string
}

export const SciteLogoSmall = ({ height = 16, className }) => {
  const width = Math.round(height * (20 / 16))
  return (
    <svg
      width={width}
      height={height}
      viewBox='0 0 20 16'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-label='scite'
    >
      <path
        d='M2.85 8.625C2.88333 9.10834 3.08333 9.54167 3.45 9.925C3.81667 10.3083 4.36667 10.5 5.1 10.5C5.65 10.5 6.05833 10.3833 6.325 10.15C6.59167 9.9 6.725 9.60833 6.725 9.275C6.725 8.99167 6.625 8.75 6.425 8.55C6.225 8.33333 5.88333 8.175 5.4 8.075L3.775 7.7C2.575 7.43333 1.7 6.975 1.15 6.325C0.6 5.65833 0.325 4.89167 0.325 4.025C0.325 3.475 0.441667 2.95833 0.675 2.475C0.908333 1.975 1.225 1.55 1.625 1.2C2.04167 0.833334 2.525 0.541667 3.075 0.325001C3.64167 0.108334 4.25833 0 4.925 0C5.79167 0 6.525 0.125001 7.125 0.375001C7.725 0.608334 8.20833 0.908334 8.575 1.275C8.95833 1.64167 9.24167 2.03333 9.425 2.45C9.60833 2.85 9.71667 3.21667 9.75 3.55L6.975 4.175C6.94167 3.99167 6.88333 3.80833 6.8 3.625C6.73333 3.425 6.61667 3.24167 6.45 3.075C6.28333 2.90833 6.075 2.775 5.825 2.675C5.59167 2.575 5.3 2.525 4.95 2.525C4.51667 2.525 4.14167 2.64167 3.825 2.875C3.525 3.10833 3.375 3.4 3.375 3.75C3.375 4.05 3.475 4.29167 3.675 4.475C3.89167 4.65833 4.16667 4.78333 4.5 4.85L6.25 5.225C7.46667 5.475 8.375 5.94167 8.975 6.625C9.59167 7.30833 9.9 8.1 9.9 9C9.9 9.5 9.8 9.99167 9.6 10.475C9.41667 10.9583 9.125 11.3917 8.725 11.775C8.325 12.1583 7.825 12.4667 7.225 12.7C6.625 12.9333 5.925 13.05 5.125 13.05C4.19167 13.05 3.40833 12.9167 2.775 12.65C2.14167 12.3833 1.625 12.0583 1.225 11.675C0.825 11.2917 0.525 10.8833 0.325 10.45C0.141667 10 0.0333333 9.6 0 9.25L2.85 8.625Z'
        fill='#0062FF'
      />
      <path d='M11.225 15.4V12.675H20V15.4H11.225Z' fill='#0062FF' />
    </svg>
  )
}

SciteLogoSmall.propTypes = {
  height: PropTypes.number,
  className: PropTypes.string
}

// Arrows
export const ArrowRightIcon = props => <Icon name='arrowRight' {...props} />
export const ArrowLeftIcon = props => <Icon name='arrowLeft' {...props} />
export const ArrowUpIcon = props => <Icon name='arrowUp' {...props} />
export const ArrowCircleRightIcon = props => <Icon name='arrowCircleRight' {...props} />
export const ArrowUpRightIcon = props => <Icon name='arrowUpRight' {...props} />

// Status
export const LockIcon = props => <Icon name='lock' {...props} />
export const CheckCircleIcon = props => <Icon name='checkCircle' {...props} />
export const TimesCircleIcon = props => <Icon name='timesCircle' {...props} />
export const PlusCircleIcon = props => <Icon name='plusCircle' {...props} />
export const ExclamationCircleIcon = props => <Icon name='exclamationCircle' {...props} />

// Other
export const FlagIcon = props => <Icon name='flag' {...props} />
export const VolumeUpIcon = props => <Icon name='volumeUp' {...props} />
export const MagicWandIcon = props => <Icon name='magicWand' {...props} />
export const CommentIcon = props => <Icon name='comment' {...props} />
export const LinkIcon = props => <Icon name='link' {...props} />
export const FilterIcon = props => <Icon name='filter' {...props} />
export const AdjustmentsIcon = props => <Icon name='adjustments' {...props} />
export const ColumnsIcon = props => <Icon name='columns' {...props} />

// Homepage feature cards
export const LiteratureReviewsIcon = props => <Icon name='literatureReviews' {...props} />

// Sidebar toggle (stateful)
export const SidebarToggleIcon = ({ expanded, ...props }) => (
  <Icon name={expanded ? 'sidebarExpanded' : 'sidebarCollapsed'} {...props} />
)

SidebarToggleIcon.propTypes = {
  expanded: PropTypes.bool
}

// Export icon names for reference
export const ICON_NAMES = Object.keys(ICON_MAP)

export default Icon
